<script setup lang="ts">
  import { Typography } from '@components/index'
  import { INatureProps, INodeComplementLinkProps } from '@/types/index'
  import loadIcon from '@utils/loadIcon'
  import successToast from '@/toasts/successToast'

  const props = defineProps<INatureProps>()
  const emit = defineEmits(['onOpenModal', 'onFavoriteClick', 'onShareClick'])
  const labels = {
    code: 'Código de Natureza:',
    purpose: 'Finalidade:'
  }

  const toast = useToast()

  const handleLinkClick = (data: INodeComplementLinkProps) => {
    emit('onOpenModal', data)
  }

  const handleCopyClick = () => {
    navigator.clipboard.writeText(props.code).then(() => {
      successToast({
        toast,
        description: 'Código de natureza copiado com sucesso',
        showIcon: false
      })
    })
  }

  const handleFavoriteClick = () => {
    emit('onFavoriteClick')
  }

  const isFavorite = computed(() => props.isFavorite ?? false)

  const shareDetail = () => {
    emit('onShareClick')
  }

  const blinkShareButtonOn = ref(false)
  const blinkTimings = [2000, 300, 150, 300]

  function blinkShareButton(arr: number[], index = 0) {
    if (index >= arr.length) return
    setTimeout(() => {
      blinkShareButtonOn.value = index % 2 === 0
      blinkShareButton(arr, index + 1)
    }, arr[index])
  }

  onMounted(() => {
    blinkShareButton(blinkTimings)
  })
</script>

<template>
  <div class="container-fluid">
    <div class="d-flex flex-row justify-content-between">
      <Typography variant="regulation-details-title">{{ props.title }}</Typography>
      <div class="details-button">
        <i
          class="icon-img ps-1"
          :class="loadIcon(isFavorite ? 'bookmark-plus-fill' : 'bookmark-plus')"
          style="font-size: 22px; cursor: pointer"
          @click="handleFavoriteClick"
        />
      </div>
    </div>
    <div class="bullet-frame rounded-3 p-3 mt-3">
      <div class="mb-3">
        <div class="d-flex justify-content-between">
          <Typography variant="regulation-details-subtitle">{{ labels.code }}</Typography>
          <button
            class="me-1 details-button system-button"
            :class="{ blink: blinkShareButtonOn }"
            @click="shareDetail"
          >
            <i
              class="bi bi-share-fill"
              style="cursor: pointer"
            />
          </button>
        </div>
        <div class="d-flex align-items-center mt-1">
          <Typography
            variant="regulation-details-code"
            class="me-2"
          >
            {{ props.code }}
          </Typography>
          <button class="details-button system-button">
            <i
              class="copy"
              :class="loadIcon('copy')"
              style="cursor: pointer"
              @click="handleCopyClick"
            />
          </button>
        </div>
      </div>
      <Typography variant="regulation-details-subtitle">{{ labels.purpose }}</Typography>
      <Typography
        variant="regulation-details-description"
        class="mb-2"
      >
        {{ props.purpose }}
      </Typography>
      <Typography
        variant="regulation-details-link"
        class="text-decoration-underline cursor-pointer"
        @click="handleLinkClick(props.reference)"
      >
        {{ props.reference.title }}
      </Typography>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .bullet-frame {
    background-color: #303338;
    border-radius: 9px;
  }

  .copy {
    font-size: 18px;
    cursor: pointer;
  }
  .details-button {
    font-size: 1.4em;
  }

  .details-button:active {
    color: #04d200;
  }

  @keyframes blink {
    50% {
      color: #04d200;
    }
  }

  .blink {
    animation: blink 0.3s ease-in-out;
  }
</style>
